const BUNDLE_WRAPPER_SELECTOR =
  ".kaching-bundles__bars .kaching-bundles__bar:nth-child(1)";
const AVP_CONTAINER_SELECTOR = ".avpoptions-container__v2";
const AVP_WAIT_TIMEOUT_MS = 30000;

// ─── Merchant-configurable text (set via window.__engravingConfig in main-product.liquid) ──
const cfg = window.__engravingConfig || {};
const TRIGGER_TEXT = cfg.triggerText || "✦ Add Engraving";
const TRIGGER_TEXT_AFTER = cfg.triggerTextAfter || "✦ Engraving Added ✓";
const LOADING_LOGO_TEXT = cfg.loadingLogoText || "Modern Viking";
const LOADING_MESSAGE = cfg.loadingMessage || "Please wait while engraving options are loading…";
const LOADING_FALLBACK_MESSAGE =
  cfg.loadingFallbackMessage ||
  "Engraving options couldn't be loaded. Please refresh the page, or contact us and we'll add your engraving to the order manually.";
const LOADING_POPUP_TIMEOUT_MS = 10000;

// ─── Elder Futhark rune map ─────────────────────────────────────────────────
const RUNE_MAP = {
  // ᚠ
  f: "ᚠ",
  // ᚢ
  u: "ᚢ",
  ø: "ᚢ",
  ü: "ᚢ",
  v: "ᚢ",
  // ᚦ
  þ: "ᚦ",
  ð: "ᚦ",
  // ᚨ
  a: "ᚨ",
  // ᚱ
  r: "ᚱ",
  ʀ: "ᚱ",
  // ᚲ
  k: "ᚲ",
  c: "ᚲ",
  q: "ᚲ",
  // ᚷ
  g: "ᚷ",
  // ᚹ
  w: "ᚹ",
  // ᚺ
  h: "ᚺ",
  // ᚾ
  n: "ᚾ",
  // ᛁ
  i: "ᛁ",
  y: "ᛁ",
  // ᛃ
  j: "ᛃ",
  // ᛈ
  p: "ᛈ",
  // ᛇ
  ï: "ᛇ",
  æ: "ᛇ",
  // ᛉ
  z: "ᛉ",
  š: "ᛉ",
  ž: "ᛉ",
  // ᛊ
  s: "ᛊ",
  // ᛏ
  t: "ᛏ",
  // ᛒ
  b: "ᛒ",
  // ᛖ
  e: "ᛖ",
  ä: "ᛖ",
  // ᛗ
  m: "ᛗ",
  // ᛚ
  l: "ᛚ",
  // ᛜ
  ŋ: "ᛜ",
  // ᛞ
  d: "ᛞ",
  // ᛟ
  o: "ᛟ",
  œ: "ᛟ",
  õ: "ᛟ",
  ö: "ᛟ",
  // × (special punctuation)
  "'": "×",
  ":": "×",
  " ": " ",
};
const translateToRunes = (str) =>
  str
    .toLowerCase()
    .split("")
    .map((ch) => RUNE_MAP[ch] || ch)
    .join("");

// ─── Shared AVP references (set once, reused by the modal) ──────────────────
let sharedOriginalSelect = null;
let sharedOriginalTextMap = {}; // textMapIdx → { container, input, maxLength }
let sharedOriginalRadios = [];

// ─── Per-trigger state ───────────────────────────────────────────────────────
const triggerStateMap = new WeakMap(); // triggerBtn → { fontIdx, lengthIdx, text }

// ─── Modal singleton ─────────────────────────────────────────────────────────
let modalEl = null;
let currentTriggerBtn = null;

// ─── AVP loading state ───────────────────────────────────────────────────────
let avpReady = false;
let pendingTriggerBtn = null;
let loadingPopupEl = null;
let loadingPopupTimeoutId = null;
let atcEngravingBtn = null;

// ─── Live preview helper ─────────────────────────────────────────────────────
// The shopper always types plain Latin into a single normal text input — this
// works with every keyboard, including mobile soft keyboards / IMEs (the old
// approach intercepted `keydown` to build a runic string, which silently
// swallowed every keystroke on mobile because IME keys report `e.key` as
// "Unidentified"). When Runic is selected we translate that Latin text to runes
// for the live preview only; the field itself stays readable Latin.
const updatePreview = () => {
  if (!modalEl) return;
  const isRunic = modalEl.fontTiles[1]?.classList.contains("selected");
  const display = modalEl.previewDisplay;
  const raw = modalEl.textInput.value || "";
  if (isRunic) {
    display.textContent = raw ? translateToRunes(raw) : "";
    display.classList.add("engraving-popup__preview--runic");
    display.classList.remove("engraving-popup__preview--modern");
  } else {
    display.textContent = raw;
    display.classList.remove("engraving-popup__preview--runic");
    display.classList.add("engraving-popup__preview--modern");
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const waitForElement = (selector, timeout = AVP_WAIT_TIMEOUT_MS) => {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const timeoutId = setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timed out waiting for: ${selector}`));
    }, timeout);
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearTimeout(timeoutId);
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
};

// ─── AVP length-option source ────────────────────────────────────────────────
// Aris renders the engraving length option either as a <select> (legacy) or as
// a radio button-group (current). Both use the same index semantics:
// index 0 = "No engraving", 1..n = letter-count tiers. Normalise them so the
// rest of this file can keep using .options / .selectedIndex either way.
const resolveLengthSource = (avpContainer) => {
  const select = avpContainer.querySelector(
    ".avp-select select[data-type='select']",
  );
  if (select) return select;

  const buttonContainer = avpContainer.querySelector(
    ".ap-options__button-container",
  );
  if (!buttonContainer) return null;

  const radios = Array.from(
    buttonContainer.querySelectorAll("input[type='radio']"),
  );
  if (radios.length === 0) return null;

  return {
    // Use the option's visible label (which the AVP app renders WITH its price,
    // e.g. "15 Letters (+$14.99)") rather than the bare radio value, so the
    // modal's letter-count tiles show the price and it visibly changes between
    // the 3- and 15-letter tiers. Falls back to the value if no label.
    options: radios.map((radio) => {
      const label = radio.closest("label");
      const text = label
        ? label.textContent.trim().replace(/\s+/g, " ")
        : radio.value || "";
      return { textContent: text, value: radio.value || "" };
    }),
    get selectedIndex() {
      return radios.findIndex((radio) => radio.checked);
    },
    set selectedIndex(idx) {
      const radio = radios[idx];
      if (!radio) return;
      radio.removeAttribute("disabled");
      if (!radio.checked) radio.click(); // fires a native change event
    },
    dispatchEvent() {
      return true; // radio.click() above already dispatched it
    },
  };
};

// Aris inserts .avpoptions-container__v2 before it populates the controls
// inside it, so waiting on the container alone can resolve against an empty
// shell. Wait for the option controls themselves.
const waitForAvpOptions = (avpContainer, timeout = AVP_WAIT_TIMEOUT_MS) => {
  return new Promise((resolve, reject) => {
    if (resolveLengthSource(avpContainer)) {
      resolve(avpContainer);
      return;
    }
    const timeoutId = setTimeout(() => {
      observer.disconnect();
      reject(new Error("Timed out waiting for AVP option controls"));
    }, timeout);
    const observer = new MutationObserver(() => {
      if (resolveLengthSource(avpContainer)) {
        clearTimeout(timeoutId);
        observer.disconnect();
        resolve(avpContainer);
      }
    });
    observer.observe(avpContainer, { childList: true, subtree: true });
  });
};

// ─── Loading popup (shown while AVP app is initialising) ─────────────────────
const buildLoadingPopup = () => {
  if (loadingPopupEl) return;

  const overlay = document.createElement("div");
  overlay.id = "engraving-loading-overlay";
  overlay.className = "hidden";

  const popup = document.createElement("div");
  popup.id = "engraving-loading-popup";

  const closeBtn = document.createElement("button");
  closeBtn.className = "engraving-popup__close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", closeLoadingPopup);

  const brand = document.createElement("div");
  brand.className = "engraving-loading__brand";
  brand.textContent = LOADING_LOGO_TEXT;

  popup.appendChild(closeBtn);
  popup.appendChild(brand);

  const message = document.createElement("p");
  message.className = "engraving-loading__message";
  message.textContent = LOADING_MESSAGE;
  popup.appendChild(message);

  overlay.appendChild(popup);
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeLoadingPopup();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !overlay.classList.contains("hidden"))
      closeLoadingPopup();
  });

  loadingPopupEl = { overlay };
};

const showLoadingPopup = (triggerBtn) => {
  if (!loadingPopupEl) buildLoadingPopup();
  pendingTriggerBtn = triggerBtn;
  loadingPopupEl.overlay.classList.remove("hidden");
  document.body.style.overflow = "hidden";

  // Never leave a shopper staring at a spinner that will never resolve. If the
  // options still aren't ready, tell them what to do instead of hanging.
  const message = loadingPopupEl.overlay.querySelector(
    ".engraving-loading__message",
  );
  if (message) message.textContent = LOADING_MESSAGE;
  clearTimeout(loadingPopupTimeoutId);
  loadingPopupTimeoutId = setTimeout(() => {
    if (avpReady || !message) return;
    message.textContent = LOADING_FALLBACK_MESSAGE;
  }, LOADING_POPUP_TIMEOUT_MS);
};

const closeLoadingPopup = () => {
  if (!loadingPopupEl) return;
  clearTimeout(loadingPopupTimeoutId);
  loadingPopupEl.overlay.classList.add("hidden");
  document.body.style.overflow = "";
  pendingTriggerBtn = null;
};

// ─── Build modal DOM (once) ───────────────────────────────────────────────────
const buildModal = () => {
  if (modalEl) return;

  const overlay = document.createElement("div");
  overlay.id = "engraving-popup-overlay";
  overlay.className = "hidden";

  const popup = document.createElement("div");
  popup.id = "engraving-popup";

  // Close button
  const closeBtn = document.createElement("button");
  closeBtn.className = "engraving-popup__close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", closeModal);

  // Heading
  const heading = document.createElement("h3");
  heading.className = "engraving-popup__heading";
  heading.textContent = "Engraving Options";

  // ── Section: Font style ──────────────────────────────────────────────────
  const fontSection = document.createElement("div");
  fontSection.className = "engraving-popup__section";

  const fontLabel = document.createElement("p");
  fontLabel.className = "engraving-popup__section-label";
  fontLabel.textContent = "Font Style";

  const fontTilesWrap = document.createElement("div");
  fontTilesWrap.className = "engraving-tiles engraving-tiles--font";

  const fontTiles = sharedOriginalRadios.map((radio, rIdx) => {
    const labelEl = radio.closest("label");
    const labelText = labelEl
      ? labelEl.textContent.trim()
      : rIdx === 0
        ? "Modern Letters"
        : "Runic Letters +$5";
    const tile = document.createElement("div");
    tile.className = "engraving-tile font-tile";
    tile.dataset.fontIdx = rIdx;
    tile.textContent = labelText;
    tile.addEventListener("click", () => selectFontTile(rIdx));
    return tile;
  });

  // Fallback font tiles if AVP has no radios
  if (fontTiles.length === 0) {
    ["Modern Letters", "Runic Letters +$5"].forEach((label, rIdx) => {
      const tile = document.createElement("div");
      tile.className = "engraving-tile font-tile";
      tile.dataset.fontIdx = rIdx;
      tile.textContent = label;
      tile.addEventListener("click", () => selectFontTile(rIdx));
      fontTiles.push(tile);
    });
  }

  fontTiles.forEach((t) => fontTilesWrap.appendChild(t));
  fontSection.appendChild(fontLabel);
  fontSection.appendChild(fontTilesWrap);

  // ── Section: Letter count (length options, skipping "No engraving") ──────
  // sharedOriginalTextMap keys correspond to AVP select option indices 1, 2, ...
  // (option 0 = "No engraving", we skip it)
  const lengthSection = document.createElement("div");
  lengthSection.className = "engraving-popup__section";

  const lengthLabel = document.createElement("p");
  lengthLabel.className = "engraving-popup__section-label";
  lengthLabel.textContent = "Letter Count";

  const lengthTilesWrap = document.createElement("div");
  lengthTilesWrap.className = "engraving-tiles engraving-tiles--length";

  // Build length tiles from AVP select options (skip index 0 = "No engraving")
  const lengthTiles = [];
  if (sharedOriginalSelect) {
    Array.from(sharedOriginalSelect.options).forEach((opt, idx) => {
      if (idx === 0) return; // skip "No engraving"
      const tile = document.createElement("div");
      tile.className = "engraving-tile length-tile";
      tile.dataset.lengthIdx = idx; // maps to select option index
      tile.textContent = opt.textContent.trim();
      tile.addEventListener("click", () => selectLengthTile(idx));
      lengthTiles.push(tile);
      lengthTilesWrap.appendChild(tile);
    });
  }

  lengthSection.appendChild(lengthLabel);
  lengthSection.appendChild(lengthTilesWrap);

  // ── Text input ────────────────────────────────────────────────────────────
  const inputArea = document.createElement("div");
  inputArea.className = "engraving-popup__input-area engraving-popup__section";

  const inputLabel = document.createElement("p");
  inputLabel.className = "engraving-popup__section-label";
  inputLabel.textContent = "Your Text";

  const textInput = document.createElement("input");
  textInput.type = "text";
  textInput.className = "engraving-popup__text-input";
  textInput.placeholder = "Enter your engraving…";
  textInput.setAttribute("autocomplete", "off");

  const charCounter = document.createElement("span");
  charCounter.className = "engraving-popup__char-counter";
  charCounter.textContent = "0";

  // Placeholder — replaced with real impl after saveBtn is created below
  let updateApplyState = () => { };

  // Single input for both fonts. On every `input` event we refresh the counter
  // and preview — this is keyboard-agnostic, so mobile soft keyboards work.
  textInput.addEventListener("input", () => {
    charCounter.textContent = String(textInput.value.length);
    updatePreview();
    updateApplyState();
  });

  inputArea.appendChild(inputLabel);
  inputArea.appendChild(textInput);
  inputArea.appendChild(charCounter);

  // ── Live preview ──────────────────────────────────────────────────────────
  const previewSection = document.createElement("div");
  previewSection.className =
    "engraving-popup__section engraving-popup__preview-section";

  const previewLabel = document.createElement("p");
  previewLabel.className = "engraving-popup__section-label";
  previewLabel.textContent = "Preview";

  const previewDisplay = document.createElement("div");
  previewDisplay.className = "engraving-popup__preview";
  previewDisplay.setAttribute("aria-live", "polite");

  previewSection.appendChild(previewLabel);
  previewSection.appendChild(previewDisplay);

  // ── Apply button ─────────────────────────────────────────────────────────
  const saveBtn = document.createElement("button");
  saveBtn.className = "engraving-popup__save";
  saveBtn.textContent = "Apply";
  saveBtn.disabled = true;
  saveBtn.addEventListener("click", applyAndClose);

  // Now that saveBtn exists, wire up the real apply-state checker
  updateApplyState = () => {
    saveBtn.disabled = textInput.value.trim().length === 0;
  };

  // ── No Engraving button ───────────────────────────────────────────────────
  const noEngravingBtn = document.createElement("button");
  noEngravingBtn.className = "engraving-popup__no-engraving";
  noEngravingBtn.textContent = "No Engraving";
  noEngravingBtn.addEventListener("click", removeEngraving);

  // ── Assemble ─────────────────────────────────────────────────────────────
  popup.appendChild(closeBtn);
  popup.appendChild(heading);
  popup.appendChild(fontSection);
  popup.appendChild(lengthSection);
  popup.appendChild(inputArea);
  popup.appendChild(previewSection);
  popup.appendChild(saveBtn);
  popup.appendChild(noEngravingBtn);
  overlay.appendChild(popup);
  document.body.appendChild(overlay);

  // Close on backdrop click
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });
  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !overlay.classList.contains("hidden"))
      closeModal();
  });

  modalEl = {
    overlay,
    fontTiles,
    lengthTiles,
    inputArea,
    textInput,
    charCounter,
    previewDisplay,
    saveBtn,
    noEngravingBtn,
    updateApplyState,
  };
};

// ─── Tile selection helpers ───────────────────────────────────────────────────
const selectFontTile = (rIdx) => {
  if (!modalEl) return;
  modalEl.fontTiles.forEach((t, i) =>
    t.classList.toggle("selected", i === rIdx),
  );
  // Font choice only changes how the preview renders (Latin vs runes) — the
  // input itself always shows the shopper's plain Latin text.
  modalEl.charCounter.textContent = String(modalEl.textInput.value.length);
  updatePreview();
  modalEl.updateApplyState();
};

const selectLengthTile = (selectOptIdx) => {
  if (!modalEl) return;
  modalEl.lengthTiles.forEach((t) =>
    t.classList.toggle(
      "selected",
      Number(t.dataset.lengthIdx) === selectOptIdx,
    ),
  );

  // Update text input max length from AVP
  const textMapIdx = selectOptIdx - 1; // textMap is 0-based from option 1
  const entry = sharedOriginalTextMap[textMapIdx];
  if (entry?.input) {
    const max = entry.input.getAttribute("maxlength") || "";
    if (max) {
      modalEl.textInput.setAttribute("maxlength", max);
      modalEl.charCounter.dataset.max = max;
      // Trim any existing overflow when switching to a shorter tier.
      if (modalEl.textInput.value.length > Number(max)) {
        modalEl.textInput.value = modalEl.textInput.value.slice(0, Number(max));
        modalEl.charCounter.textContent = String(modalEl.textInput.value.length);
        updatePreview();
        modalEl.updateApplyState();
      }
    } else {
      modalEl.textInput.removeAttribute("maxlength");
      modalEl.charCounter.dataset.max = "";
    }
  }
};

// ─── Open / close ─────────────────────────────────────────────────────────────
const openModal = (triggerBtn) => {
  if (!modalEl) return;
  currentTriggerBtn = triggerBtn;

  // Restore previous state or apply defaults
  const state = triggerStateMap.get(triggerBtn);
  const fontIdx = state?.fontIdx ?? 0;
  const lengthIdx =
    state?.lengthIdx ??
    (modalEl.lengthTiles[0]
      ? Number(modalEl.lengthTiles[0].dataset.lengthIdx)
      : 1);
  const savedText = state?.text ?? "";

  // The single Latin input holds the text for both fonts.
  modalEl.textInput.value = savedText;
  selectLengthTile(lengthIdx);
  selectFontTile(fontIdx);
  modalEl.charCounter.textContent = String(modalEl.textInput.value.length);
  updatePreview();
  modalEl.updateApplyState();

  modalEl.overlay.classList.remove("hidden");
  document.body.style.overflow = "hidden";
};

const closeModal = () => {
  if (!modalEl) return;
  modalEl.overlay.classList.add("hidden");
  document.body.style.overflow = "";
  currentTriggerBtn = null;
};

// ─── Apply: sync to AVP, update trigger label ─────────────────────────────────
const applyAndClose = () => {
  if (!modalEl || !sharedOriginalSelect) return;

  const selectedLengthTile = modalEl.lengthTiles.find((t) =>
    t.classList.contains("selected"),
  );
  const selectedOptIdx = selectedLengthTile
    ? Number(selectedLengthTile.dataset.lengthIdx)
    : 1;

  const selectedFontTile = modalEl.fontTiles.find((t) =>
    t.classList.contains("selected"),
  );
  const fontIdx = selectedFontTile
    ? Number(selectedFontTile.dataset.fontIdx)
    : 0;

  // The Latin text is what the engraving team works from, for both fonts.
  const enteredText = modalEl.textInput.value.trim();

  // Sync AVP select (length / pricing option)
  sharedOriginalSelect.selectedIndex = selectedOptIdx;
  sharedOriginalSelect.dispatchEvent(new Event("change", { bubbles: true }));

  // Sync text inputs
  Object.keys(sharedOriginalTextMap).forEach((key) => {
    const { input } = sharedOriginalTextMap[Number(key)];
    const isActive = Number(key) === selectedOptIdx - 1;
    input.value = isActive ? enteredText : "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  // Sync font radio
  if (sharedOriginalRadios.length > 0) {
    const origRadio = sharedOriginalRadios[fontIdx];
    if (origRadio) {
      origRadio.removeAttribute("disabled");
      if (!origRadio.checked) {
        origRadio.click(); // browser sets checked + fires change naturally
      } else {
        origRadio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  // Save state
  if (currentTriggerBtn) {
    triggerStateMap.set(currentTriggerBtn, {
      fontIdx,
      lengthIdx: selectedOptIdx,
      text: enteredText,
    });
    updateTriggerLabel(currentTriggerBtn, selectedOptIdx, enteredText);
  }

  closeModal();
};

// ─── Remove engraving: reset AVP + trigger label ─────────────────────────────
const removeEngraving = () => {
  if (!sharedOriginalSelect) return;

  // Reset AVP select to "No engraving" (index 0)
  sharedOriginalSelect.selectedIndex = 0;
  sharedOriginalSelect.dispatchEvent(new Event("change", { bubbles: true }));

  // Clear all AVP text inputs
  Object.keys(sharedOriginalTextMap).forEach((key) => {
    const { input } = sharedOriginalTextMap[Number(key)];
    input.value = "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  // Reset trigger button label and clear saved state
  if (currentTriggerBtn) {
    currentTriggerBtn.textContent = TRIGGER_TEXT;
    currentTriggerBtn.classList.remove("has-engraving");
    triggerStateMap.delete(currentTriggerBtn);
  }
  if (atcEngravingBtn) {
    atcEngravingBtn.textContent = TRIGGER_TEXT;
    atcEngravingBtn.classList.remove("has-engraving");
  }

  closeModal();
};

// ─── Update trigger label ─────────────────────────────────────────────────────
const updateTriggerLabel = (btn, selectedOptIdx, text) => {
  btn.textContent = TRIGGER_TEXT_AFTER;
  btn.classList.add("has-engraving");
  if (atcEngravingBtn) {
    atcEngravingBtn.textContent = TRIGGER_TEXT_AFTER;
    atcEngravingBtn.classList.add("has-engraving");
  }
};

// ─── Bundle contents toggle (show/hide product list per bar) ──────────────────
const initBundleToggle = () => {
  const barsContainer = document.querySelector(".kaching-bundles__bars");
  if (!barsContainer) return;

  const injectToggle = (bar) => {
    if (bar.dataset.toggleInjected === "true") return;
    bar.dataset.toggleInjected = "true";

    const wrapper = bar.querySelector(".kaching-bundles__bar-wrapper");
    if (!wrapper) return;

    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "bundle-toggle-btn";
    toggleBtn.innerHTML =
      '<span class="bundle-toggle-icon">▾</span><span class="bundle-toggle-label">See what\'s included</span>';
    toggleBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const expanded = bar.classList.toggle("bundle-expanded");
      toggleBtn.querySelector(".bundle-toggle-label").textContent = expanded
        ? "Hide items"
        : "See what's included";
      toggleBtn.querySelector(".bundle-toggle-icon").style.transform = expanded
        ? "rotate(180deg)"
        : "";
    });

    wrapper.appendChild(toggleBtn);
  };

  // Inject toggle on all currently selected bars and watch for future selections
  const observer = new MutationObserver(() => {
    barsContainer
      .querySelectorAll(".kaching-bundles__bar--selected")
      .forEach(injectToggle);
  });
  observer.observe(barsContainer, {
    subtree: true,
    attributeFilter: ["class"],
    attributes: true,
  });

  // Also handle any already-selected bar on load
  barsContainer
    .querySelectorAll(".kaching-bundles__bar--selected")
    .forEach(injectToggle);
  // And all bars (so toggle appears even before selection, hidden via CSS)
  barsContainer.querySelectorAll(".kaching-bundles__bar").forEach(injectToggle);
};

// ─── Bundle option image swap (per-bar product image) ─────────────────────────
const initBundleImageSwap = () => {
  const images = window.__bundleOptionImages;
  if (!images || images.length === 0) return;

  const barsContainer = document.querySelector(".kaching-bundles__bars");
  if (!barsContainer) return;

  // Cache the first slide's <img> and its wrapper
  const firstSlide = document.querySelector(
    ".product-main-slide.starting-slide",
  );
  if (!firstSlide) return;

  const mainImg =
    firstSlide.querySelector("img.photoswipe__image") ||
    firstSlide.querySelector("img.image-element");
  if (!mainImg) return;

  const imageWrap = mainImg.closest(".image-wrap");

  // Also grab the first thumbnail image (index 0) if present
  const thumbItem = document.querySelector(
    '.product__thumb-item[data-index="0"] img',
  );

  const swapImage = (barIndex) => {
    // index 0 = default product image, 1..n = metafield images
    // Fall back to default (index 0) when no image exists for this bar
    const data = images[barIndex] || images[0];
    if (!data) return;

    mainImg.setAttribute("src", data.src);
    mainImg.setAttribute("srcset", data.srcset);
    mainImg.setAttribute("data-photoswipe-src", data.zoom);
    mainImg.setAttribute("data-photoswipe-width", data.width);
    mainImg.setAttribute("data-photoswipe-height", data.height);

    // Update aspect-ratio padding on the wrapper
    if (imageWrap && data.width && data.height) {
      imageWrap.style.paddingBottom =
        (100 / (data.width / data.height)).toFixed(4) + "%";
    }

    // Mirror change on the first thumbnail
    if (thumbItem) {
      thumbItem.setAttribute("src", data.src);
      if (thumbItem.hasAttribute("srcset")) {
        thumbItem.setAttribute("srcset", data.srcset);
      }
    }
  };

  // Observe class changes to detect bar selection
  const detectAndSwap = () => {
    const bars = Array.from(
      barsContainer.querySelectorAll(".kaching-bundles__bar"),
    );
    const selected = barsContainer.querySelector(
      ".kaching-bundles__bar--selected",
    );
    const idx = selected ? bars.indexOf(selected) : 0;
    swapImage(idx >= 0 ? idx : 0);
  };

  const observer = new MutationObserver(detectAndSwap);
  observer.observe(barsContainer, {
    subtree: true,
    attributeFilter: ["class"],
    attributes: true,
  });

  // Handle already-selected bar on initial load
  detectAndSwap();
};

// ─── Inject trigger buttons into bundle bars (no AVP needed) ─────────────────
const injectTriggerButtons = (bundlesWrapper) => {
  bundlesWrapper.forEach((wrapper) => {
    if (!wrapper.matches(".kaching-bundles__bar")) return;

    const barWrapper = wrapper.querySelector(".kaching-bundles__bar-wrapper");
    if (!barWrapper || barWrapper.dataset.engravingInjected === "true") return;
    barWrapper.dataset.engravingInjected = "true";

    const triggerBtn = document.createElement("button");
    triggerBtn.type = "button";
    triggerBtn.className = "engraving-trigger-btn";
    triggerBtn.textContent = TRIGGER_TEXT;
    triggerBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (avpReady) {
        openModal(triggerBtn);
      } else {
        showLoadingPopup(triggerBtn);
      }
    });

    // Insert inside barWrapper directly after the bar-main row
    const bundleProducts = barWrapper.querySelector(
      ".kaching-bundles__bundle-products",
    );
    const barMain = barWrapper.querySelector(".kaching-bundles__bar-main");
    const bundleHighlight = barWrapper.querySelector(
      ".kaching-bundles__highlights",
    );
    if (bundleHighlight) {
      barWrapper.insertBefore(triggerBtn, bundleHighlight);
    } else if (bundleProducts) {
      barWrapper.insertBefore(triggerBtn, bundleProducts);
    } else if (barMain) {
      barMain.insertAdjacentElement("afterend", triggerBtn);
    } else {
      barWrapper.appendChild(triggerBtn);
    }
  });
};

// ─── Wire up AVP references and build the real modal (called once AVP loads) ──
const setupAvpAndModal = (avpContainer) => {
  const originalSelect = resolveLengthSource(avpContainer);
  if (!originalSelect) return;

  sharedOriginalSelect = originalSelect;

  const originalTextContainers = Array.from(
    avpContainer.querySelectorAll(".ap-options__text-container"),
  );
  originalTextContainers.forEach((container, idx) => {
    const input = container.querySelector("input[type='text']");
    if (input) sharedOriginalTextMap[idx] = { container, input };
  });

  const originalRadioContainer = avpContainer.querySelector(
    ".ap-options__radio-container",
  );
  sharedOriginalRadios = originalRadioContainer
    ? Array.from(originalRadioContainer.querySelectorAll("input[type='radio']"))
    : [];

  buildModal();
  avpReady = true;

  // Guard the bundle add-to-cart against the Avis × Kaching upsell conflict.
  bindAtcRescue();

  // If the loading popup is open for a pending trigger, transition to the real modal
  if (pendingTriggerBtn) {
    const btn = pendingTriggerBtn;
    closeLoadingPopup();
    openModal(btn);
  }
};

// ─── Kept for re-injection calls from the mutation observer ──────────────────
const buildEngravingTriggers = (bundlesWrapper, avpContainer) => {
  if (avpContainer) setupAvpAndModal(avpContainer);
  injectTriggerButtons(bundlesWrapper);
};

// ─── Inject a single "Add Engraving" button below the Add to Cart button ──────
const injectAtcEngravingBtn = () => {
  const atcContainer = document.querySelector(".atc-container");
  if (!atcContainer || atcContainer.dataset.engravingAtcInjected === "true")
    return;
  atcContainer.dataset.engravingAtcInjected = "true";

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "engraving-trigger-btn engraving-trigger-btn--atc";
  btn.textContent = TRIGGER_TEXT;
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Delegate to the currently selected bundle bar's trigger button
    const selectedBar = document.querySelector(
      ".kaching-bundles__bar--selected",
    );
    const barTrigger = selectedBar
      ? selectedBar.querySelector(".engraving-trigger-btn:not(.engraving-trigger-btn--atc)")
      : document.querySelector(".engraving-trigger-btn:not(.engraving-trigger-btn--atc)");
    if (barTrigger) {
      barTrigger.click();
    } else if (avpReady) {
      openModal(btn);
    } else {
      showLoadingPopup(btn);
    }
  });

  atcContainer.appendChild(btn);
  // Label sync is handled by updateTriggerLabel / removeEngraving via atcEngravingBtn
  atcEngravingBtn = btn;
};

// ═══════════════════════════════════════════════════════════════════════════
// ATC RESCUE — Avis × Kaching add-to-cart conflict
// ═══════════════════════════════════════════════════════════════════════════
//
// PROBLEM
//   The Avis (AVP) options app intercepts the add-to-cart the moment an engraving
//   option is selected and submits ONLY the main product. That pre-empts Kaching
//   Bundles' own click handler, so any selected upsell (e.g. "Add the Care Kit")
//   is silently dropped. Without engraving, Kaching runs normally and the upsell
//   is added, which is why the bug only shows up once you personalise.
//
// FIX
//   When engraving is active AND an upsell is selected we take over the ATC click
//   and post ONE /cart/add.js ourselves: the main product plus every selected
//   upsell. This works for every bar type because Kaching always adds a single
//   main product — quantity-break bars use the page product, while "bundle" bars
//   swap the form's id to their pre-composed bundle product — so the form's
//   current id + properties are always the correct main line. Two things make it
//   safe:
//     • Avis patches fetch/XHR at the network level, so it still injects the
//       engraving properties + surcharge onto the main line of our request.
//     • Each upsell carries a rebuilt __kaching_bundles property that references
//       the main line's group id, so Kaching's bundle discount still applies.
//   Non-engraving adds are left to Kaching untouched, as before.

const KACHING_DEAL_SETTINGS_SELECTOR =
  "script.kaching-bundles-deal-block-settings";

let atcRescueBound = false;
let controlledAddInFlight = false;

const uuidV4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

// Engraving is "active" — i.e. Avis will hijack the ATC — when a length option
// other than "No engraving" (index 0) is currently selected. This is read live
// from the AVP control, so it reflects the modal's Apply / "No Engraving".
const isEngravingActive = () =>
  !!sharedOriginalSelect && sharedOriginalSelect.selectedIndex > 0;

// Resolve the config deal bar for a rendered bar element by its position.
// We match by DOM order (rendered bars mirror the config's dealBars order)
// because "bundle" bars don't carry a `bid` in the form property, so we can't
// look them up by id. Prefer the settings block whose dealBars count matches
// the number of rendered bars; fall back to the first parseable block.
const getKachingBarForElement = (barEl) => {
  const bars = Array.from(document.querySelectorAll(".kaching-bundles__bar"));
  const idx = bars.indexOf(barEl);
  if (idx < 0) return null;

  const scripts = document.querySelectorAll(KACHING_DEAL_SETTINGS_SELECTOR);
  const parsed = [];
  for (const script of scripts) {
    try {
      const json = JSON.parse(script.textContent);
      if (Array.isArray(json.dealBars)) parsed.push(json.dealBars);
    } catch (e) {
      /* not this one */
    }
  }
  const exact = parsed.find((d) => d.length === bars.length);
  if (exact && exact[idx]) return exact[idx];
  const any = parsed.find((d) => d[idx]);
  return any ? any[idx] : null;
};

// Decide synchronously whether we should take over this click, and gather the
// bits needed to build the payload. Returns null to let Avis/Kaching run.
const getAtcRescueContext = () => {
  if (!isEngravingActive()) return null;

  const selectedBar = document.querySelector(".kaching-bundles__bar--selected");
  if (!selectedBar) return null;

  // Only the reported failure mode: an upsell is selected on the current bar.
  if (
    !selectedBar.querySelector(".kaching-bundles__upsell__checkbox--selected")
  )
    return null;

  const form = document.querySelector(
    '.product-single__form, form[action*="/cart/add"]',
  );
  if (!form) return null;

  const kbInput = form.querySelector('[name="properties[__kaching_bundles]"]');
  let mainKb = null;
  try {
    mainKb = JSON.parse(kbInput.value);
  } catch (e) {
    /* no bundle context */
  }
  // Every bar type carries `id` + `deal` (the group id + discount) even though
  // "bundle" bars omit `bid`. Those two are all we need to link the upsell line.
  if (!mainKb || !mainKb.id || !mainKb.deal) return null;

  // Works for every bar type: quantity-break bars add the page product and
  // "bundle" bars swap the form to their pre-composed bundle product — either
  // way Kaching adds ONE main product (the form's current id) plus the upsell,
  // so the same reconstruction applies.
  const bar = getKachingBarForElement(selectedBar);
  if (!bar) return null;

  return { form, selectedBar, mainKb, bar };
};

// Resolve an upsell's variant id: prefer the configured variant, else the first
// available variant of the linked product.
const resolveUpsellVariantId = async (upsellEl, upsellCfg) => {
  if (
    upsellCfg &&
    Array.isArray(upsellCfg.variantGIDs) &&
    upsellCfg.variantGIDs.length
  ) {
    const id = Number(String(upsellCfg.variantGIDs[0]).split("/").pop());
    if (id) return id;
  }
  const link = upsellEl.querySelector('a[href*="/products/"]');
  if (!link) return null;
  try {
    const handle = new URL(link.href, location.origin).pathname
      .split("/products/")[1]
      .split(/[/?#]/)[0];
    const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
    const variant =
      product.variants.find((v) => v.available) || product.variants[0];
    return variant ? variant.id : null;
  } catch (e) {
    return null;
  }
};

// Build the /cart/add.js items[] payload: main product (from the form, so Avis's
// network patch injects the engraving) + each selected, resolvable upsell.
const buildRescuePayload = async (ctx) => {
  const fd = new FormData(ctx.form);
  const mainId = fd.get("id");
  if (!mainId) return null;

  const mainProps = {};
  for (const [key, value] of fd.entries()) {
    const m = key.match(/^properties\[(.+)\]$/);
    if (m) mainProps[m[1]] = value;
  }

  const upsellEls = Array.from(
    ctx.selectedBar.querySelectorAll(".kaching-bundles__upsell"),
  );
  const upsellsCfg = ctx.bar.upsells || [];

  const upsellItems = [];
  for (let i = 0; i < upsellEls.length; i++) {
    const el = upsellEls[i];
    const checkbox = el.querySelector(".kaching-bundles__upsell__checkbox");
    if (
      !checkbox ||
      !checkbox.classList.contains(
        "kaching-bundles__upsell__checkbox--selected",
      )
    )
      continue;

    const ucfg = upsellsCfg[i];
    const variantId = await resolveUpsellVariantId(el, ucfg);
    if (!variantId) continue;

    upsellItems.push({
      id: variantId,
      quantity: (ucfg && ucfg.quantity) || 1,
      properties: {
        // Tag the upsell so Kaching applies the bundle discount: it shares the
        // main line's group `id` + `deal` and points at the upsell def. We give
        // it its OWN `v` (a fresh uuid) rather than the main's: a matching `v`
        // makes Kaching's own cart script try to manage a bundle it didn't
        // create, which re-adds a duplicate upsell on removal. With a distinct
        // `v` the line is simply orphaned when the main is removed, and our
        // cleanupOrphanUpsells() (below) removes it — see bindAtcRescue().
        __kaching_bundles: JSON.stringify({
          id: ctx.mainKb.id,
          deal: ctx.mainKb.deal,
          v: uuidV4(),
          upsell: ucfg ? ucfg.id : undefined,
        }),
      },
    });
  }

  if (!upsellItems.length) return null; // nothing we could add — abort takeover

  const mainQty =
    Number(ctx.bar.quantity) || Number(fd.get("quantity")) || 1;

  return [
    { id: Number(mainId), quantity: mainQty, properties: mainProps },
    ...upsellItems,
  ];
};

// Capture-phase click handler on document — runs before Avis's/Kaching's own
// button listeners, so we can cleanly take over when needed.
const onAtcRescueClick = (e) => {
  const btn = e.target.closest(
    '.atc-container [name="add"], .atc-container button.add-to-cart',
  );
  if (!btn) return;

  const ctx = getAtcRescueContext();
  if (!ctx) return; // not our case — let Avis/Kaching handle it

  // We are taking over. Block Avis's hijack, Kaching's handler and the native
  // submit, then perform the full add ourselves.
  e.preventDefault();
  e.stopImmediatePropagation();

  if (controlledAddInFlight) return;
  controlledAddInFlight = true;

  buildRescuePayload(ctx)
    .then((items) => {
      if (!items) return null; // couldn't build — leave cart untouched, unlock below
      return fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });
    })
    .then((res) => {
      if (!res) return; // nothing built — leave the cart untouched
      if (!res.ok) throw new Error("cart/add failed: " + res.status);
      // Rebuild the drawer contents (ajaxProduct:added) AND force it open
      // (cart:open) — theme.CartDrawer listens for both.
      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", { detail: {} }),
      );
      document.dispatchEvent(new CustomEvent("cart:open"));
    })
    .catch((err) => console.log("Engraving bundle ATC rescue failed:", err))
    .finally(() => {
      controlledAddInFlight = false;
    });
};

// ─── Orphaned-upsell cleanup ──────────────────────────────────────────────────
// An upsell we added via the rescue is NOT tracked by Kaching's runtime, so
// Kaching won't cascade-remove it when the shopper deletes its main line — it
// would linger (and re-adding the bundle would then stack a second copy). This
// watchdog removes any bundle upsell line whose group has no main line left.
// It keys on the group `id` shared by a main and its upsell(s); a genuine
// Kaching bundle is never affected because Kaching removes its own upsells
// first (so no orphan remains to match).
let cleaningOrphans = false;

const cleanupOrphanUpsells = async () => {
  if (cleaningOrphans) return;
  cleaningOrphans = true;
  try {
    const cart = await fetch("/cart.js").then((r) => r.json());
    const parseKb = (item) => {
      try {
        return JSON.parse(item.properties.__kaching_bundles);
      } catch (e) {
        return null;
      }
    };

    // Collect the group ids that still have a main line present.
    const mainGroupIds = new Set();
    cart.items.forEach((item) => {
      const kb = parseKb(item);
      if (kb && kb.main && kb.id != null) mainGroupIds.add(kb.id);
    });

    // Any upsell line whose group lost its main is orphaned → remove it.
    const updates = {};
    cart.items.forEach((item) => {
      const kb = parseKb(item);
      if (kb && kb.upsell != null && kb.id != null && !mainGroupIds.has(kb.id)) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates }),
    });
    // Re-render the drawer so the removed line disappears.
    document.dispatchEvent(new CustomEvent("cart:build"));
  } catch (e) {
    console.log("Orphan upsell cleanup failed:", e);
  } finally {
    cleaningOrphans = false;
  }
};

const bindAtcRescue = () => {
  if (atcRescueBound) return;
  atcRescueBound = true;
  document.addEventListener("click", onAtcRescueClick, true);
  // Sweep orphaned upsells whenever the cart re-renders (e.g. after the shopper
  // removes a main line in the drawer).
  document.addEventListener("cart:updated", cleanupOrphanUpsells);
  document.addEventListener("cart:build", cleanupOrphanUpsells);
  // And once on load, in case an orphan survived from an earlier visit.
  cleanupOrphanUpsells();
};

// ─── Entry point ──────────────────────────────────────────────────────────────
const createEngraving = () => {
  try {
    const productWrapper = document.querySelector(".product-single__form");
    if (!productWrapper) throw new Error("product wrapper does not exist");

    waitForElement(BUNDLE_WRAPPER_SELECTOR)
      .then(() => {
        const wrappers = Array.from(
          document.querySelectorAll(
            ".kaching-bundles__bars .kaching-bundles__bar",
          ),
        );
        if (wrappers.length === 0) {
          console.log("This is not a bundle product");
          return;
        }

        // Init bundle contents toggle (independent of AVP)
        initBundleToggle();

        // Init bundle option image swap (independent of AVP)
        initBundleImageSwap();

        // Immediately inject trigger buttons and ATC button — no AVP needed
        buildLoadingPopup();
        injectTriggerButtons(wrappers);
        injectAtcEngravingBtn();

        // Set up re-injection observer so new bars get buttons too
        const barsContainer = document.querySelector(".kaching-bundles__bars");
        if (barsContainer) {
          let reinjectTimer = null;
          const engravingObserver = new MutationObserver(() => {
            clearTimeout(reinjectTimer);
            reinjectTimer = setTimeout(() => {
              const allBars = Array.from(
                barsContainer.querySelectorAll(".kaching-bundles__bar"),
              );
              injectTriggerButtons(allBars);
            }, 200);
          });
          engravingObserver.observe(barsContainer, {
            childList: true,
            subtree: true,
          });
        }

        // Load AVP in the background — enables the real modal when ready
        waitForElement(AVP_CONTAINER_SELECTOR, AVP_WAIT_TIMEOUT_MS)
          .then((avpContainer) => waitForAvpOptions(avpContainer))
          .then((avpContainer) => setupAvpAndModal(avpContainer))
          .catch((err) =>
            console.log("Engraving: AVP options never became ready:", err),
          );
      })
      .catch((err) => console.log("Error in engraving setup:", err));
  } catch (e) {
    console.log("Error:", e);
  } finally {
    console.log("Engraving function finished executing");
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    if (typeof createEngraving === "function") createEngraving();
  });
} else {
  if (typeof createEngraving === "function") createEngraving();
}
